import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, Check, Copy, X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn'
import { DURATION, easeOutExpo, modalBackdrop, modalContent, slideUp } from './motion'

// ─────────────────────────── Badge ───────────────────────────

type BadgeTone = 'neutral' | 'accent' | 'positive' | 'warning' | 'danger'

/**
 * Mapped onto the semantic tones rather than the old good/warning/bad ramp: `positive` is
 * fluid (stables, flow), `warning` is brass (reward, caps) and `danger` is fault. Each pairs a
 * darkened `-text` token with the vivid `-edge` for the ring — using the material hue as text
 * is the 2.1:1 mistake theme.css warns about.
 */
const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'text-faint ring-line',
  accent: 'text-accent ring-accent-edge',
  positive: 'text-fluid-text ring-fluid-edge',
  warning: 'text-brass-text ring-brass-edge',
  danger: 'text-fault-text ring-fault-edge',
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode
  tone?: BadgeTone
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ring-1',
        'font-mono text-[10px] font-medium uppercase tracking-[0.1em] whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

// ─────────────────────────── Skeleton ───────────────────────────

/**
 * Skeletons must match the final content's box exactly. A skeleton that is the wrong
 * height causes a layout shift the instant data arrives, which is worse than no
 * skeleton at all.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      // NOT `bg-elevated` — see the --color-skeleton note in theme.css.
      className={cn('animate-pulse rounded-[2px] bg-skeleton', className)}
      aria-hidden
    />
  )
}

// ─────────────────────────── States ───────────────────────────

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <motion.div
      // Transform only. `fadeIn` puts opacity 0 in the first frame, which a frozen timeline
      // makes permanent — an empty state that is itself invisible tells the reader nothing.
      initial={{ y: 8 }}
      animate={{ y: 0 }}
      transition={{ duration: DURATION.base, ease: easeOutExpo }}
      className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center"
    >
      {icon && <div className="text-faint">{icon}</div>}
      <p className="display text-lg text-ink">{title}</p>
      {description && <p className="max-w-sm text-sm text-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </motion.div>
  )
}

export function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <AlertTriangle className="size-6 text-danger" aria-hidden />
      <p className="max-w-sm text-sm text-muted">{message}</p>
      {retry && (
        <button
          onClick={retry}
          className="text-sm font-medium text-accent underline underline-offset-4"
        >
          Try again
        </button>
      )}
    </div>
  )
}

// ─────────────────────────── Callout ───────────────────────────

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warning' | 'danger'
  title?: string
  children: ReactNode
}) {
  /*
   * The landing page's callout strip: a coloured rule down the left edge, a mono uppercase
   * title in the tone, and body copy in plain muted prose. The icon is gone — the rule already
   * carries the tone, and on a light surface an icon plus a tint plus a ring was three
   * signals for one piece of information.
   */
  const rule = { info: 'bg-accent', warning: 'bg-brass', danger: 'bg-fault' }[tone]
  const heading = {
    info: 'text-accent',
    warning: 'text-brass-text',
    danger: 'text-fault-text',
  }[tone]

  return (
    <div className="surface-glass-2 relative overflow-hidden rounded-[10px] p-4 pl-5 ring-1 ring-line">
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-[3px]', rule)} />
      <div className="min-w-0 text-sm">
        {title && (
          <p className={cn('font-mono text-[11px] font-medium uppercase tracking-[0.1em]', heading)}>
            {title}
          </p>
        )}
        <div className={cn('leading-relaxed text-muted', title && 'mt-1.5')}>{children}</div>
      </div>
    </div>
  )
}

// ─────────────────────────── Modal ───────────────────────────

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
}) {
  // Escape closes; body scroll locks while open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose])

  /**
   * Rendered into <body> via a portal, NOT in place.
   *
   * `position: fixed` is relative to the nearest ancestor with a `filter`,
   * `backdrop-filter` or `transform` — not the viewport. Our sticky header uses
   * `backdrop-blur`, so a modal opened from the header (the wallet picker) would
   * anchor to the 56px-tall header and render half off-screen with no backdrop.
   */
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          <motion.div
            variants={modalBackdrop}
            initial="initial"
            animate="animate"
            exit="exit"
            onClick={onClose}
            // `scrim/40`, not `black/60` — pure black over a silver hall reads as a hole
            // punched through the page rather than as the page dimmed behind a dialog.
            className="absolute inset-0 bg-scrim/40 backdrop-blur-sm"
          />
          <motion.div
            variants={modalContent}
            initial="initial"
            animate="animate"
            exit="exit"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className="surface-glass lift relative z-10 w-full max-w-lg rounded-t-[18px] ring-1 ring-line sm:rounded-[18px]"
          >
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <h2 className="display text-base">{title}</h2>
              <button
                onClick={onClose}
                aria-label="Close"
                className="rounded-sm p-1 text-muted hover:bg-elevated hover:text-ink"
              >
                <X className="size-4" />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
            {footer && <div className="border-t border-line p-5">{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

// ─────────────────────────── Tabs ───────────────────────────

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { id: T; label: string }[]
  value: T
  onChange: (id: T) => void
}) {
  return (
    <div
      role="tablist"
      className="inline-flex gap-1 rounded-[10px] p-1"
      style={{ background: 'var(--track-bg)', boxShadow: 'var(--track-shadow)' }}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={value === tab.id}
          onClick={() => onChange(tab.id)}
          className={cn(
            'relative rounded-[2px] px-3 py-1 transition-colors',
            'font-mono text-[10px] font-medium uppercase tracking-[0.1em]',
            value === tab.id ? 'text-ink' : 'text-muted hover:text-ink',
          )}
        >
          {value === tab.id && (
            // layoutId gives the indicator a real slide between tabs, for free.
            <motion.span
              layoutId="tab-indicator"
              className="absolute inset-0 rounded-[2px]"
              style={{ background: 'var(--m-steel)', boxShadow: 'var(--plate-shadow)' }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            />
          )}
          <span className="relative">{tab.label}</span>
        </button>
      ))}
    </div>
  )
}

// ─────────────────────────── CopyButton ───────────────────────────

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  return (
    <button
      onClick={() => {
        void navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1600)
      }}
      aria-label={label ?? 'Copy'}
      className="rounded-sm p-1 text-faint transition-colors hover:bg-elevated hover:text-ink"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span key={copied ? 'done' : 'idle'} variants={slideUp} initial="initial" animate="animate" exit="exit">
          {copied ? (
            <Check className="size-3.5 text-positive" />
          ) : (
            <Copy className="size-3.5" />
          )}
        </motion.span>
      </AnimatePresence>
    </button>
  )
}
